import { useState, useCallback, useEffect } from 'react';
import { Loader2, RefreshCw, Save, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QuestionCard } from './QuestionCard';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import * as apiClient from '@/services/apiClient';

/**
 * QuestionSetEditorModal
 *
 * Full editor for a saved question set.
 * All edits are local until "Save Set" is clicked.
 *
 * Props:
 *   open      boolean
 *   onClose   () => void
 *   set       QuestionSet  (full object with questions[])
 *   onSaved   (updatedSet) => void
 */
export function QuestionSetEditorModal({ open, onClose, set, onSaved }) {
    // Local copy of questions — changes stay local until "Save Set"
    const [questions,     setQuestions]     = useState([]);
    const [saving,        setSaving]        = useState(false);
    const [regenAllLoading, setRegenAll]    = useState(false);
    const [regenAllConfirmOpen, setRegenAllConfirmOpen] = useState(false);
    const [regenSingleId, setRegenSingleId] = useState(null); // qId being regenerated
    const [dirty,         setDirty]         = useState(false);

    // Initialise local copy from the set prop
    const initFromSet = useCallback((s) => {
        if (!s) return;
        setQuestions(s.questions?.map((q, i) => ({ ...q, order: i })) || []);
        setDirty(false);
    }, []);

    // ── Key fix: Radix UI's onOpenChange only fires when the dialog tries to
    // CLOSE itself (Escape / overlay click). It does NOT fire when the parent
    // externally sets open=true. Use a useEffect instead so initFromSet always
    // runs when the dialog becomes visible with a new set prop.
    useEffect(() => {
        if (open && set) initFromSet(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, set]);

    // Called by Dialog's onOpenChange (only handles close gestures)
    const handleOpenChange = (isOpen) => {
        if (!isOpen) onClose();
    };

    // ── Local mutation helpers ──────────────────────────────────────────────

    const applyEdit = (idx, updated) => {
        setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, ...updated } : q));
        setDirty(true);
    };

    const deleteQuestion = (idx) => {
        setQuestions(prev => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i })));
        setDirty(true);
    };

    const moveUp = (idx) => {
        if (idx === 0) return;
        setQuestions(prev => {
            const next = [...prev];
            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
            return next.map((q, i) => ({ ...q, order: i }));
        });
        setDirty(true);
    };

    const moveDown = (idx) => {
        setQuestions(prev => {
            if (idx >= prev.length - 1) return prev;
            const next = [...prev];
            [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
            return next.map((q, i) => ({ ...q, order: i }));
        });
        setDirty(true);
    };

    const addBlank = () => {
        const blank = {
            _id:             undefined,
            questionText:    '',
            questionType:    'technical',
            difficulty:      set?.difficultyName?.toLowerCase() || 'medium',
            timeLimitSeconds: 300,
            idealAnswer:     '',
            keywords:        [],
            order:           questions.length,
        };
        setQuestions(prev => [...prev, blank]);
        setDirty(true);
    };

    // ── AI regenerate single question ───────────────────────────────────────

    const regenSingle = async (idx) => {
        const q = questions[idx];
        if (!q?._id) {
            toast.error('Save the set first before regenerating a new question.');
            return;
        }
        setRegenSingleId(String(q._id));
        try {
            const res = await apiClient.regenerateSingleQuestion(set._id, String(q._id));
            setQuestions(prev => prev.map((item, i) =>
                i === idx ? { ...item, ...res.data } : item
            ));
            setDirty(true);
            toast.success('Question regenerated.');
        } catch (err) {
            toast.error(err.message || 'Failed to regenerate question.');
        } finally {
            setRegenSingleId(null);
        }
    };

    // ── Regenerate ALL questions ────────────────────────────────────────────

    const regenAll = async () => {
        setRegenAll(true);
        try {
            const res = await apiClient.regenerateAllQuestions(set._id);
            // Replace local questions with freshly generated ones
            const newQs = res.data?.questions || [];
            setQuestions(newQs.map((q, i) => ({ ...q, order: i })));
            setDirty(false); // server already saved the new questions
            toast.success(`All ${newQs.length} questions regenerated.`);
        } catch (err) {
            toast.error(err.message || 'Failed to regenerate questions.');
        } finally {
            setRegenAll(false);
        }
    };

    // ── Save ────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (questions.length === 0) {
            toast.error('A question set must have at least one question.');
            return;
        }
        setSaving(true);
        try {
            const res = await apiClient.updateQuestionSetQuestions(set._id, questions);
            setDirty(false);
            initFromSet(res.data);
            onSaved?.(res.data);
            toast.success(`Saved ${res.data.questions?.length} questions.`);
        } catch (err) {
            toast.error(err.message || 'Failed to save questions.');
        } finally {
            setSaving(false);
        }
    };

    if (!set) return null;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[92vh] w-[95vw] overflow-hidden flex flex-col sm:max-w-4xl p-0">
                {/* ── Header ────────────────────────────────────────────── */}
                <DialogHeader className="px-6 pt-5 pb-4 pr-14 border-b border-border flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <DialogTitle className="text-base font-semibold truncate">{set.name}</DialogTitle>
                            <DialogDescription className="text-xs mt-0.5">
                                {questions.length} question{questions.length !== 1 ? 's' : ''}
                                {dirty && <span className="ml-2 text-amber-600 font-medium">• Unsaved changes</span>}
                            </DialogDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
                                onClick={() => setRegenAllConfirmOpen(true)} disabled={regenAllLoading || saving}>
                                {regenAllLoading
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <RefreshCw className="h-3.5 w-3.5" />}
                                Regenerate all
                            </Button>
                            <Button size="sm" className="h-8 gap-1.5 text-xs"
                                onClick={handleSave} disabled={saving || !dirty}>
                                {saving
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Save className="h-3.5 w-3.5" />}
                                Save set
                            </Button>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Question list ──────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {questions.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
                            <AlertTriangle className="h-8 w-8 opacity-40" />
                            <p className="text-sm">No questions yet. Add one below.</p>
                        </div>
                    )}

                    {questions.map((q, idx) => (
                        <QuestionCard
                            key={q._id ? String(q._id) : `new-${idx}`}
                            question={q}
                            index={idx}
                            total={questions.length}
                            onEdit={(upd) => applyEdit(idx, upd)}
                            onDelete={() => deleteQuestion(idx)}
                            onMoveUp={() => moveUp(idx)}
                            onMoveDown={() => moveDown(idx)}
                            onRegen={() => regenSingle(idx)}
                            regenLoading={regenSingleId === String(q._id)}
                            disabled={saving}
                        />
                    ))}

                    {/* Add blank question */}
                    <button
                        type="button"
                        onClick={addBlank}
                        disabled={saving}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border py-3 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add question manually
                    </button>
                </div>
            </DialogContent>

            <ConfirmDialog
                open={regenAllConfirmOpen}
                onOpenChange={setRegenAllConfirmOpen}
                title="Regenerate all questions?"
                description="This will replace all questions with new AI-generated ones. Unsaved edits will be lost. The AI will use the same type, difficulty, and question count as when the set was created."
                confirmLabel="Regenerate all"
                cancelLabel="Cancel"
                loading={regenAllLoading}
                onConfirm={async () => {
                    setRegenAllConfirmOpen(false);
                    await regenAll();
                }}
            />
        </Dialog>
    );
}

export default QuestionSetEditorModal;
